import { useCallback, useEffect, useRef } from 'react'
import { useTimelineStore } from '@/stores/timeline-store'

export const PLAYBACK_SPEEDS = [0.5, 1, 2, 5, 10] as const
export type PlaybackSpeed = (typeof PLAYBACK_SPEEDS)[number]

/**
 * "Play through a time range" controller for the bottom temporal strip.
 *
 * Implementation is a plain rAF loop that advances `playheadTime` between
 * `filterStart` and `filterEnd` on the `useTimelineStore`. Earlier
 * revisions had a Cesium-coupled path that drove the playhead from
 * `viewer.clock`, but the app no longer mounts a Cesium viewer — the
 * live map is MapLibre — so that branch was always dormant and has been
 * removed.
 */
export interface PlaybackController {
  /** Whether the playhead is currently advancing. */
  isPlaying: boolean
  /** Current playhead time in ms epoch, or `null` when no range is set. */
  playheadTime: number | null
  /** Playback speed multiplier (1x default). */
  speed: number

  onPlayPause: () => void
  onStepBack: () => void
  onStepForward: () => void
  onSkipToStart: () => void
  onSkipToEnd: () => void
  onSpeedChange: (next: number) => void
  /** Seek the playhead to a specific ms-epoch time. */
  onSeek: (ms: number) => void
}

/**
 * Pick a playback speed that makes a full range complete in a sane
 * wall-clock time when the user selects 1×. Short ranges (< 1 h) get
 * 30 s; long ranges (> 30 d) get 180 s. Same heuristic as the original
 * Cesium-coupled implementation so the perceived speed is unchanged.
 */
function basePlaybackSeconds(rangeSec: number): number {
  if (rangeSec < 3600) return 30
  if (rangeSec < 86400) return 60
  if (rangeSec < 604800) return 90
  if (rangeSec < 2592000) return 120
  return 180
}
function speedToMultiplier(factor: number, rangeSec: number): number {
  return (rangeSec / basePlaybackSeconds(rangeSec)) * factor
}

/**
 * `fallbackBounds` lets the caller plug in the view's natural temporal extent
 * (e.g. the map's animated-entity bounds, or the cockpit's globalTimeRange)
 * so playback works the moment a Play button is visible — without requiring
 * the user to click Auto-fit first. Without it, fresh map loads and
 * filter→playback toggles would land with `filterStart/filterEnd` still null
 * and `onPlayPause` would silently no-op. The store filter (when present)
 * still wins so an active filter window keeps scoping playback.
 */
export function usePlaybackController(
  fallbackBounds?: { start: number; end: number } | null
): PlaybackController {
  const filterStart = useTimelineStore(s => s.filterStart)
  const filterEnd = useTimelineStore(s => s.filterEnd)
  const playheadIso = useTimelineStore(s => s.playheadTime)
  const isPlaying = useTimelineStore(s => s.isPlaying)
  const playSpeed = useTimelineStore(s => s.playSpeed)
  const setPlayheadTime = useTimelineStore(s => s.setPlayheadTime)
  const setIsPlaying = useTimelineStore(s => s.setIsPlaying)
  const setPlaySpeed = useTimelineStore(s => s.setPlaySpeed)

  const fallbackStart = fallbackBounds?.start
  const fallbackEnd = fallbackBounds?.end

  const getBounds = useCallback(() => {
    if (filterStart && filterEnd) {
      const startMs = new Date(filterStart).getTime()
      const endMs = new Date(filterEnd).getTime()
      if (Number.isFinite(startMs) && Number.isFinite(endMs)) return { startMs, endMs }
    }
    if (
      fallbackStart !== undefined &&
      fallbackEnd !== undefined &&
      Number.isFinite(fallbackStart) &&
      Number.isFinite(fallbackEnd)
    ) {
      return { startMs: fallbackStart, endMs: fallbackEnd }
    }
    return null
  }, [filterStart, filterEnd, fallbackStart, fallbackEnd])

  // rAF playback engine. Started by `onPlayPause`, stopped on pause /
  // end-of-range / unmount. Reads the latest playhead/speed from the
  // store every frame so manual seeks while playing pick up cleanly.
  const rafRef = useRef<number>(0)
  const lastFrameRef = useRef<number>(0)

  const stopRaf = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = 0
    }
  }, [])

  const startRaf = useCallback(() => {
    stopRaf()
    lastFrameRef.current = performance.now()
    const tick = () => {
      const bounds = getBounds()
      if (!bounds) return
      const now = performance.now()
      const dt = (now - lastFrameRef.current) / 1000
      lastFrameRef.current = now
      const rangeSec = (bounds.endMs - bounds.startMs) / 1000
      const multiplier = speedToMultiplier(useTimelineStore.getState().playSpeed, rangeSec)
      const advanceMs = dt * multiplier * 1000
      const phIso = useTimelineStore.getState().playheadTime
      const curMs = phIso ? new Date(phIso).getTime() : bounds.startMs
      const newMs = curMs + advanceMs
      if (newMs >= bounds.endMs) {
        setPlayheadTime(new Date(bounds.endMs).toISOString())
        setIsPlaying(false)
        return
      }
      setPlayheadTime(new Date(newMs).toISOString())
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [getBounds, setPlayheadTime, setIsPlaying, stopRaf])

  useEffect(() => stopRaf, [stopRaf])

  const onPlayPause = useCallback(() => {
    const bounds = getBounds()
    if (!bounds) return
    if (isPlaying) {
      stopRaf()
      setIsPlaying(false)
      return
    }
    // Starting up — if the playhead is at/past the end, rewind to start.
    const curMs = playheadIso ? new Date(playheadIso).getTime() : bounds.startMs
    if (curMs >= bounds.endMs - 100) {
      setPlayheadTime(new Date(bounds.startMs).toISOString())
    }
    startRaf()
    setIsPlaying(true)
  }, [isPlaying, playheadIso, getBounds, setIsPlaying, setPlayheadTime, startRaf, stopRaf])

  const onSkipToStart = useCallback(() => {
    const bounds = getBounds()
    if (!bounds) return
    stopRaf()
    setIsPlaying(false)
    setPlayheadTime(new Date(bounds.startMs).toISOString())
  }, [getBounds, setIsPlaying, setPlayheadTime, stopRaf])

  const onSkipToEnd = useCallback(() => {
    const bounds = getBounds()
    if (!bounds) return
    stopRaf()
    setIsPlaying(false)
    setPlayheadTime(new Date(bounds.endMs).toISOString())
  }, [getBounds, setIsPlaying, setPlayheadTime, stopRaf])

  const step = useCallback(
    (direction: 1 | -1) => {
      const bounds = getBounds()
      if (!bounds) return
      const stepMs = (bounds.endMs - bounds.startMs) * 0.01 * direction
      const curMs = playheadIso ? new Date(playheadIso).getTime() : bounds.startMs
      const newMs = Math.max(bounds.startMs, Math.min(bounds.endMs, curMs + stepMs))
      setPlayheadTime(new Date(newMs).toISOString())
    },
    [getBounds, playheadIso, setPlayheadTime]
  )
  const onStepBack = useCallback(() => step(-1), [step])
  const onStepForward = useCallback(() => step(1), [step])

  const onSpeedChange = useCallback(
    (factor: number) => {
      setPlaySpeed(factor)
    },
    [setPlaySpeed]
  )

  const onSeek = useCallback(
    (ms: number) => {
      const bounds = getBounds()
      if (!bounds) return
      const clamped = Math.max(bounds.startMs, Math.min(bounds.endMs, ms))
      setPlayheadTime(new Date(clamped).toISOString())
    },
    [getBounds, setPlayheadTime]
  )

  const playheadMs = playheadIso ? new Date(playheadIso).getTime() : null

  return {
    isPlaying,
    playheadTime: Number.isFinite(playheadMs) ? playheadMs : null,
    speed: playSpeed,
    onPlayPause,
    onStepBack,
    onStepForward,
    onSkipToStart,
    onSkipToEnd,
    onSpeedChange,
    onSeek,
  }
}
